/**
 * Internal tool to keep the client-facing translation catalog in sync.
 *
 * Three steps: update an existing catalog with whatever the app gained since it was
 * produced, hand it to the client, and turn the filled-in file back into the JSON for
 * the `Institution.custom_i18n_overrides` backend setting.
 *
 * Not linked from the sidebar on purpose — it is reached by direct URL.
 * Its copy is intentionally hardcoded: adding i18n keys here would leak an
 * `i18n-catalog` sheet into the very catalog handed to the client.
 */

import { FC, useMemo } from "react";
import { CardNotification, Heading, Text } from "@foris/avocado-suite";
import { i18n_KEY_FILES } from "@config/i18n/files";
import {
  buildCatalogRows,
  getBundledLocales,
  getCatalogStats,
  type LocaleMessages,
} from "@utils/i18nCatalog";
import GenerateCatalogSection from "../../components/generate-catalog-section";
import OverridesSection from "../../components/overrides-section";
import UpdateCatalogSection from "../../components/update-catalog-section";
import { SETTING_NAME } from "../../constants";
// Layout is the only place that pulls the design system stylesheet in, and this
// page renders outside of it, so it has to load the vendor styles itself.
import "@assets/css/vendor.scss";
import styles from "../../i18nCatalog.module.scss";

/**
 * Locale JSON files sitting on disk but not wired into `i18n_KEY_FILES`. Their keys
 * reach neither the catalog nor `applyLocaleOverrides`, so they need registering.
 * Lives here rather than in utils because `import.meta.glob` is Vite-only syntax.
 */
function findUnregisteredLocaleFiles(): string[] {
  const modules = import.meta.glob("/src/locale/es/*.json", { eager: true }) as Record<
    string,
    { default: LocaleMessages }
  >;
  const registeredMainKeys = new Set(
    Object.values(i18n_KEY_FILES).flatMap(file => Object.keys(file.es ?? {})),
  );

  return Object.entries(modules)
    .filter(([, module]) => {
      const mainKeys = Object.keys(module?.default ?? {});

      return mainKeys.length > 0 && mainKeys.every(mainKey => !registeredMainKeys.has(mainKey));
    })
    .map(([path]) => path.replace("/src/locale/es/", ""))
    .sort();
}

const I18nCatalog: FC = () => {
  const locales = useMemo(() => getBundledLocales(), []);
  const catalogStats = useMemo(() => getCatalogStats(buildCatalogRows(locales)), [locales]);
  const unregisteredFiles = useMemo(() => findUnregisteredLocaleFiles(), []);

  const hasDiagnostics = !!catalogStats.unnamedMainKeys.length || !!unregisteredFiles.length;

  return (
    <div className={styles.i18nCatalog}>
      <header className={styles.i18nCatalog_header}>
        <Heading type="h1">Catálogo de traducciones</Heading>
        <Text type="md">
          Mantiene el Excel que se le envía al cliente para personalizar los textos de la
          aplicación, y lo convierte al JSON que consume el setting {SETTING_NAME}.
        </Text>
      </header>

      {hasDiagnostics && (
        <CardNotification state="warning" title="Hay keys que necesitan atención" outlined>
          {!!catalogStats.unnamedMainKeys.length && (
            <>
              <Text type="sm">
                Estas secciones no tienen nombre legible en VIEW_NAMES, así que su pestaña en el
                Excel sale con el nombre técnico. Agrégalas en utils/i18nCatalog.ts:
              </Text>
              <ul className={styles.i18nCatalog_list}>
                {catalogStats.unnamedMainKeys.map(mainKey => (
                  <li key={mainKey}>
                    <Text type="sm">{mainKey}</Text>
                  </li>
                ))}
              </ul>
            </>
          )}

          {!!unregisteredFiles.length && (
            <>
              <Text type="sm">
                Estos archivos de locale no están registrados en config/i18n/files.ts, así que sus
                textos no llegan ni al catálogo ni a los overrides:
              </Text>
              <ul className={styles.i18nCatalog_list}>
                {unregisteredFiles.map(fileName => (
                  <li key={fileName}>
                    <Text type="sm">{fileName}</Text>
                  </li>
                ))}
              </ul>
            </>
          )}
        </CardNotification>
      )}

      <UpdateCatalogSection locales={locales} />
      <OverridesSection locales={locales} />
      <GenerateCatalogSection locales={locales} stats={catalogStats} />
    </div>
  );
};

export default I18nCatalog;
